import { AlertTriangle, Info, Send } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Controller } from "react-hook-form";
import { z } from "zod";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import { ColocaInput } from "@/shared/components/forms/ColocaInput";
import { ColocaSelect } from "@/shared/components/forms/ColocaSelect";
import { useZodForm } from "@/shared/components/forms/useZodForm";
import { ColocaDrawer } from "@/shared/components/overlays/ColocaDrawer";
import { useTranslations } from "@/shared/lang/useTranslations";
import { colocaToast, toastApiError } from "@/shared/utils/toast";

import { INVITATION_LANGUAGES } from "../../constants/accessCatalog.constants";
import { useAccessCan } from "../../hooks/useAccessCan";
import { useRolesData } from "../../hooks/useAccessData";
import { invitationsService } from "../../services/invitationsService";
import type { InvitationLanguage } from "../../types/access.types";
import { defaultReadOnlyRoleIds } from "../../utils/inviteDefaultRoles";
import { RolePickerList } from "./RolePickerList";

export interface InviteUserDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after the invitation is created and emailed — parent closes the drawer. */
  onInvited: () => void;
}

/** Native names on purpose — a language's own name needs no translation (same as LanguageSection). */
const LANGUAGE_LABELS: Record<InvitationLanguage, string> = {
  es: "Español",
  en: "English",
  pt: "Português",
};

/** The invitation email is written in one of the three supported locales; default to the inviter's UI one. */
function toInvitationLanguage(lang: string | undefined): InvitationLanguage {
  if (lang?.startsWith("en")) return "en";
  if (lang?.startsWith("pt")) return "pt";
  return "es";
}

/**
 * "Invite a person" side panel (same drawer pattern as `RoleEditorDrawer`). RHF + Zod over the exact
 * `CreateInvitationDto` contract: email (required, ≤255), optional display name (≤255) and the
 * invitation-email language. On submit → `POST /internal/invitations` — the backend emails a single-use
 * accept link and the person only appears in the users table after accepting, so no list reload is needed
 * here.
 *
 * **Roles are picked here and applied on accept.** The invitation carries `role_ids`, and
 * `InvitationsService` assigns exactly those the moment the person accepts — so an invitee lands ready to
 * work instead of read-only and waiting for a second gesture. Leaving the picker empty keeps the old
 * behaviour: the read-only default, with access granted later through `AssignRolesDrawer`.
 *
 * The legacy `role` enum is still not sent: the backend writes it to `users.role`, which no guard reads and
 * which is being deprecated. Access comes from the RBAC roles below, nothing else.
 */
export function InviteUserDrawer({ open, onOpenChange, onInvited }: InviteUserDrawerProps) {
  const { t, activeLanguage } = useTranslations("settings");

  const schema = useMemo(
    () =>
      z.object({
        email: z
          .string()
          .trim()
          .min(1, t("access_invite_email_required"))
          .max(255, t("access_invite_field_max"))
          .email(t("access_invite_email_invalid")),
        full_name: z.string().trim().max(255, t("access_invite_field_max")),
        preferred_language: z.enum(INVITATION_LANGUAGES),
      }),
    [t],
  );

  const form = useZodForm(schema, {
    email: "",
    full_name: "",
    preferred_language: toInvitationLanguage(activeLanguage),
  });
  const emailValue = form.watch("email");

  const [isSending, setIsSending] = useState(false);
  // Outside the Zod schema on purpose: the picker owns a set of ids with no field-level validation to run,
  // and the only rule (roles must be visible to the inviter) is the backend's to enforce.
  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>([]);
  const [hasSeededRoles, setHasSeededRoles] = useState(false);

  const { roles, isLoading: rolesLoading, hasNoAccess: rolesNoAccess } = useRolesData();
  const { canAll } = useAccessCan();
  // Attaching roles needs `roles:write`, the same permission the backend demands. Without it the picker is
  // not rendered at all rather than shown and refused on submit.
  const canAssignRoles = canAll("roles:write") && !rolesNoAccess;

  // Re-seed on every open so a previously typed (or submitted) invite never leaks into the next one.
  useEffect(() => {
    if (!open) return;
    form.reset({
      email: "",
      full_name: "",
      preferred_language: toInvitationLanguage(activeLanguage),
    });
    setSelectedRoleIds([]);
    setHasSeededRoles(false);
  }, [open, form, activeLanguage]);

  // Tick the read-only default once the catalog is in — and only once per open, so a role the inviter
  // unticks stays unticked instead of coming back when the roles list re-renders.
  useEffect(() => {
    if (!open || hasSeededRoles || roles.length === 0) return;
    setSelectedRoleIds(defaultReadOnlyRoleIds(roles));
    setHasSeededRoles(true);
  }, [open, hasSeededRoles, roles]);

  const languageOptions = useMemo(
    () => INVITATION_LANGUAGES.map((lang) => ({ value: lang, label: LANGUAGE_LABELS[lang] })),
    [],
  );

  const onValid = async (values: z.output<typeof schema>) => {
    setIsSending(true);
    try {
      const invitation = await invitationsService.create({
        email: values.email,
        // Empty string → omit: the field is optional in the DTO and "" would fail nothing but store noise.
        full_name: values.full_name || undefined,
        // `role` is deliberately not sent — the backend defaults it and derives no permission from it.
        preferred_language: values.preferred_language,
        // Omit rather than send [] so the backend takes its "no roles" path explicitly.
        role_ids: selectedRoleIds.length > 0 ? selectedRoleIds : undefined,
      });
      colocaToast.success(t("access_invite_success", { email: invitation.email }));
      onInvited();
    } catch (e) {
      toastApiError(e);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <ColocaDrawer
      open={open}
      onOpenChange={onOpenChange}
      size="md"
      title={t("access_invite_title")}
      description={t("access_invite_sub")}
      footer={
        <div className="flex w-full items-center justify-end gap-2">
          <ColocaButton variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            {t("access_invite_cancel")}
          </ColocaButton>
          <ColocaButton
            variant="primary"
            size="sm"
            icon={Send}
            loading={isSending}
            disabled={!emailValue.trim()}
            onClick={form.handleSubmit(onValid)}
          >
            {t("access_invite_submit")}
          </ColocaButton>
        </div>
      }
    >
      {/* Email */}
      <Controller
        control={form.control}
        name="email"
        render={({ field, fieldState }) => (
          <div className="mb-4">
            <label
              htmlFor="invite-user-email"
              className="mb-1 block text-[12.5px] font-medium text-foreground"
            >
              {t("access_invite_email_label")}
            </label>
            <ColocaInput
              id="invite-user-email"
              ref={field.ref}
              type="email"
              autoComplete="off"
              value={field.value}
              onValueChange={field.onChange}
              onBlur={field.onBlur}
              placeholder={t("access_invite_email_placeholder")}
              aria-invalid={!!fieldState.error}
            />
            {fieldState.error && (
              <p className="mt-1 flex items-center gap-1.5 text-[11.5px] text-destructive">
                <AlertTriangle size={12} /> {fieldState.error.message}
              </p>
            )}
          </div>
        )}
      />

      {/* Full name (optional, pre-fills the accept form) */}
      <Controller
        control={form.control}
        name="full_name"
        render={({ field, fieldState }) => (
          <div className="mb-4">
            <label
              htmlFor="invite-user-name"
              className="mb-1 block text-[12.5px] font-medium text-foreground"
            >
              {t("access_invite_name_label")}
            </label>
            <ColocaInput
              id="invite-user-name"
              ref={field.ref}
              value={field.value}
              onValueChange={field.onChange}
              onBlur={field.onBlur}
              placeholder={t("access_invite_name_placeholder")}
              aria-invalid={!!fieldState.error}
            />
            {fieldState.error && (
              <p className="mt-1 flex items-center gap-1.5 text-[11.5px] text-destructive">
                <AlertTriangle size={12} /> {fieldState.error.message}
              </p>
            )}
          </div>
        )}
      />

      {/* Invitation-email language */}
      <Controller
        control={form.control}
        name="preferred_language"
        render={({ field }) => (
          <div className="mb-4">
            <label
              htmlFor="invite-user-language"
              className="mb-1 block text-[12.5px] font-medium text-foreground"
            >
              {t("access_invite_language_label")}
            </label>
            <ColocaSelect
              id="invite-user-language"
              value={field.value}
              onValueChange={field.onChange}
              options={languageOptions}
            />
            <p className="mt-1 text-[11.5px] text-muted-foreground">{t("access_invite_language_hint")}</p>
          </div>
        )}
      />

      {/* Roles the invitee lands with. Hidden without `roles:write`: the backend refuses `role_ids` from a
          caller who cannot assign roles, so offering the picker would only produce a 403 on submit. */}
      {canAssignRoles && (
        <div className="mb-4">
          <span className="mb-1 block text-[12.5px] font-medium text-foreground">
            {t("access_invite_roles_label")}
          </span>
          <p className="mb-2 text-[11.5px] text-muted-foreground">{t("access_invite_roles_hint")}</p>
          {rolesLoading ? (
            <ColocaSkeleton className="h-[180px] w-full" />
          ) : (
            <RolePickerList
              roles={roles}
              selected={selectedRoleIds}
              onChange={setSelectedRoleIds}
              disabled={isSending}
            />
          )}
        </div>
      )}

      {/* What the invitee lands with when no role is picked, and where access is granted afterwards. */}
      <div className="flex items-start gap-2.5 rounded-md border border-border bg-muted/40 p-3 text-[12px] leading-relaxed text-muted-foreground">
        <Info size={14} className="mt-0.5 shrink-0" />
        <span>
          {selectedRoleIds.length > 0
            ? t("access_invite_access_note_with_roles", { count: selectedRoleIds.length })
            : t("access_invite_access_note")}
        </span>
      </div>
    </ColocaDrawer>
  );
}
